// Dynamic imports for heavy libraries (loaded on-demand)
let Chart, jsPDF, XLSX;

// Load Chart.js library asynchronously (only when chart tab is activated)
async function loadChartLibrary() {
    if (!Chart) {
        const chartModule = await import('chart.js/auto');
        Chart = chartModule.default;
    }
    return Chart;
}

// Load jsPDF library asynchronously (only when PDF download is requested)
async function loadPDFLibrary() {
    if (!jsPDF) {
        const jspdfModule = await import('jspdf');
        jsPDF = jspdfModule.jsPDF;
        await import('jspdf-autotable');
    }
    return jsPDF;
}

// Load XLSX library asynchronously (only when Excel download is requested)
async function loadXLSXLibrary() {
    if (!XLSX) {
        const xlsxModule = await import('xlsx');
        XLSX = xlsxModule;
    }
    return XLSX;
}

function loanEmiCalculator() {
    return {
        // Input Data
        loanAmount: 1000000,
        interestRate: 10,
        tenureYears: 10,
        emiType: 'monthly', // 'monthly', 'quarterly', 'yearly'
        startDate: new Date().toISOString().split('T')[0],
        
        // Advanced Options
        showAdvanced: false,
        processingFee: 0,
        processingFeeType: 'percentage', // 'percentage' or 'fixed'
        gstRate: 18,
        downPayment: 0,
        prepaymentAmount: 0,
        prepaymentFrequency: 'yearly', // 'monthly', 'yearly', 'onetime'
        prepaymentStartMonth: 12,
        extraEmi: 0,
        
        // Currency Settings
        currency: 'USD', // Auto-detected or user selected
        currencies: {
            'NONE': { symbol: '', name: 'No Currency Symbol', locale: 'en-US', pdfSymbol: '', position: 'prefix' },
            'USD': { symbol: '$', name: 'US Dollar', locale: 'en-US', pdfSymbol: '$', position: 'prefix' },
            'EUR': { symbol: '€', name: 'Euro', locale: 'de-DE', pdfSymbol: 'EUR', position: 'postfix' },
            'GBP': { symbol: '£', name: 'British Pound', locale: 'en-GB', pdfSymbol: 'GBP', position: 'prefix' },
            'INR': { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN', pdfSymbol: 'Rs.', position: 'prefix' },
            'AUD': { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU', pdfSymbol: 'A$', position: 'prefix' },
            'CAD': { symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA', pdfSymbol: 'C$', position: 'prefix' },
            'JPY': { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP', pdfSymbol: 'JPY', position: 'prefix' },
            'CNY': { symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN', pdfSymbol: 'CNY', position: 'prefix' },
            'SGD': { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG', pdfSymbol: 'S$', position: 'prefix' },
            'AED': { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE', pdfSymbol: 'AED', position: 'postfix' },
            'CHF': { symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH', pdfSymbol: 'CHF', position: 'postfix' },
            'SEK': { symbol: 'kr', name: 'Swedish Krona', locale: 'sv-SE', pdfSymbol: 'SEK', position: 'postfix' },
            'NOK': { symbol: 'kr', name: 'Norwegian Krone', locale: 'nb-NO', pdfSymbol: 'NOK', position: 'postfix' },
            'DKK': { symbol: 'kr', name: 'Danish Krone', locale: 'da-DK', pdfSymbol: 'DKK', position: 'postfix' },
            'NZD': { symbol: 'NZ$', name: 'New Zealand Dollar', locale: 'en-NZ', pdfSymbol: 'NZ$', position: 'prefix' },
            'HKD': { symbol: 'HK$', name: 'Hong Kong Dollar', locale: 'zh-HK', pdfSymbol: 'HK$', position: 'prefix' },
            'KRW': { symbol: '₩', name: 'South Korean Won', locale: 'ko-KR', pdfSymbol: 'KRW', position: 'prefix' },
            'MXN': { symbol: 'MX$', name: 'Mexican Peso', locale: 'es-MX', pdfSymbol: 'MX$', position: 'prefix' },
            'BRL': { symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR', pdfSymbol: 'R$', position: 'prefix' },
            'ZAR': { symbol: 'R', name: 'South African Rand', locale: 'en-ZA', pdfSymbol: 'ZAR', position: 'prefix' },
            'RUB': { symbol: '₽', name: 'Russian Ruble', locale: 'ru-RU', pdfSymbol: 'RUB', position: 'postfix' },
            'TRY': { symbol: '₺', name: 'Turkish Lira', locale: 'tr-TR', pdfSymbol: 'TRY', position: 'prefix' },
            'THB': { symbol: '฿', name: 'Thai Baht', locale: 'th-TH', pdfSymbol: 'THB', position: 'prefix' },
            'MYR': { symbol: 'RM', name: 'Malaysian Ringgit', locale: 'ms-MY', pdfSymbol: 'RM', position: 'prefix' },
            'IDR': { symbol: 'Rp', name: 'Indonesian Rupiah', locale: 'id-ID', pdfSymbol: 'Rp', position: 'prefix' },
            'PHP': { symbol: '₱', name: 'Philippine Peso', locale: 'fil-PH', pdfSymbol: 'PHP', position: 'prefix' },
            'PLN': { symbol: 'zł', name: 'Polish Zloty', locale: 'pl-PL', pdfSymbol: 'PLN', position: 'postfix' },
            'SAR': { symbol: 'ر.س', name: 'Saudi Riyal', locale: 'ar-SA', pdfSymbol: 'SAR', position: 'postfix' },
            'EGP': { symbol: 'E£', name: 'Egyptian Pound', locale: 'ar-EG', pdfSymbol: 'EGP', position: 'prefix' }
        },
        
        // Calculated Results
        monthlyEmi: 0,
        totalInterest: 0,
        totalAmount: 0,
        effectiveRate: 0,
        principalAmount: 0,
        interestAmount: 0,
        savingsFromPrepayment: 0,
        actualTenure: 0,
        
        // Processing Fee Calculation
        totalProcessingFee: 0,
        processingFeeWithGst: 0,
        
        // Amortization Data
        amortizationSchedule: [],
        amortizationWithoutPrepayment: [],
        
        // UI State
        activeTab: 'summary', // 'summary', 'table', 'chart'
        history: [],
        chart: null,
        chartLoading: false,
        pdfLoading: false,
        excelLoading: false,
        
        init() {
            // Load history from localStorage
            const saved = localStorage.getItem('emi_history');
            if (saved) this.history = JSON.parse(saved);
            
            // Load saved currency preference first (takes priority)
            const savedCurrency = localStorage.getItem('emi_currency');
            if (savedCurrency && this.currencies[savedCurrency]) {
                this.currency = savedCurrency;
            } else {
                // Auto-detect currency from browser locale if no saved preference
                this.detectCurrency();
            }
            
            // Calculate immediately without waiting for heavy libraries
            this.calculate();
        },

        detectCurrency() {
            // Detect currency from browser locale
            const locale = navigator.language || navigator.userLanguage || 'en-US';
            
            // Map common locales to currencies
            const localeMap = {
                'en-US': 'USD', 'en-GB': 'GBP', 'en-IN': 'INR', 'en-AU': 'AUD', 'en-CA': 'CAD',
                'de': 'EUR', 'fr': 'EUR', 'es': 'EUR', 'it': 'EUR', 'nl': 'EUR', 'de-CH': 'CHF',
                'ja': 'JPY', 'zh': 'CNY', 'zh-CN': 'CNY', 'zh-TW': 'CNY',
                'ar-AE': 'AED', 'en-SG': 'SGD',
                'sv': 'SEK', 'sv-SE': 'SEK', 'nb': 'NOK', 'nb-NO': 'NOK', 'da': 'DKK', 'da-DK': 'DKK',
                'en-NZ': 'NZD', 'zh-HK': 'HKD', 'ko': 'KRW', 'ko-KR': 'KRW',
                'es-MX': 'MXN', 'pt-BR': 'BRL', 'pt': 'BRL',
                'en-ZA': 'ZAR', 'ru': 'RUB', 'ru-RU': 'RUB', 'tr': 'TRY', 'tr-TR': 'TRY',
                'th': 'THB', 'th-TH': 'THB', 'ms': 'MYR', 'ms-MY': 'MYR',
                'id': 'IDR', 'id-ID': 'IDR', 'fil': 'PHP', 'fil-PH': 'PHP',
                'pl': 'PLN', 'pl-PL': 'PLN', 'ar-SA': 'SAR', 'ar-EG': 'EGP',
                'hi': 'INR', 'hi-IN': 'INR', 'bn': 'INR', 'te': 'INR', 'ta': 'INR', 'mr': 'INR'
            };
            
            // Try exact match first
            if (localeMap[locale]) {
                this.currency = localeMap[locale];
                return;
            }
            
            // Try language code only (e.g., 'en' from 'en-US')
            const lang = locale.split('-')[0];
            if (localeMap[lang]) {
                this.currency = localeMap[lang];
                return;
            }
            
            // Check timezone as additional hint for India
            try {
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (timezone && timezone.includes('Asia/Kolkata') || timezone.includes('Asia/Calcutta')) {
                    this.currency = 'INR';
                    return;
                }
            } catch (e) {
                // Timezone detection failed, continue
            }
            
            // Default to USD if not detected
            this.currency = 'USD';
        },

        changeCurrency() {
            // Save currency preference
            localStorage.setItem('emi_currency', this.currency);
            // Recalculate to update display
            this.calculate();
        },

        calculate() {
            // Calculate processing fee
            if (this.processingFeeType === 'percentage') {
                this.totalProcessingFee = (this.loanAmount * this.processingFee) / 100;
            } else {
                this.totalProcessingFee = parseFloat(this.processingFee) || 0;
            }
            this.processingFeeWithGst = this.totalProcessingFee * (1 + this.gstRate / 100);
            
            // Actual loan amount after down payment
            const actualLoan = this.loanAmount - this.downPayment;
            this.principalAmount = actualLoan;
            
            // Monthly interest rate
            const r = this.interestRate / 12 / 100;
            const n = this.tenureYears * 12;
            
            // EMI Calculation
            if (r > 0) {
                this.monthlyEmi = (actualLoan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
            } else {
                this.monthlyEmi = actualLoan / n;
            }
            
            // Add extra EMI
            const actualEmi = this.monthlyEmi + (parseFloat(this.extraEmi) || 0);
            
            // Generate amortization schedules
            this.generateAmortization(actualLoan, r, actualEmi);
            
            // Calculate effective interest rate
            const totalCost = this.totalAmount + this.processingFeeWithGst;
            this.effectiveRate = ((totalCost - this.loanAmount) / this.loanAmount / this.tenureYears * 100).toFixed(2);
            
            // Update chart if visible
            if (this.activeTab === 'chart') {
                this.$nextTick(() => this.renderChart());
            }
        },

        generateAmortization(principal, monthlyRate, emi) {
            let balance = principal;
            let totalInterestPaid = 0;
            let month = 0;
            const schedule = [];
            const prepayment = parseFloat(this.prepaymentAmount) || 0;
            
            // Also calculate without prepayment for comparison
            let balanceNoPrepay = principal;
            let totalInterestNoPrepay = 0;
            const scheduleNoPrepay = [];
            
            while (balance > 1 && month < 360) { // Max 30 years
                month++;
                
                // Interest for this month
                const interest = balance * monthlyRate;
                let principalPaid = emi - interest;
                
                // Apply prepayment
                let prepaymentThisMonth = 0;
                if (prepayment > 0 && month >= this.prepaymentStartMonth) {
                    if (this.prepaymentFrequency === 'monthly') {
                        prepaymentThisMonth = prepayment;
                    } else if (this.prepaymentFrequency === 'yearly' && month % 12 === 0) {
                        prepaymentThisMonth = prepayment;
                    } else if (this.prepaymentFrequency === 'onetime' && month === this.prepaymentStartMonth) {
                        prepaymentThisMonth = prepayment;
                    }
                }
                
                principalPaid += prepaymentThisMonth;
                
                // Ensure we don't overpay
                if (principalPaid > balance) {
                    principalPaid = balance;
                }
                
                balance -= principalPaid;
                totalInterestPaid += interest;
                
                schedule.push({
                    month,
                    emi: emi,
                    principal: principalPaid - prepaymentThisMonth,
                    interest: interest,
                    prepayment: prepaymentThisMonth,
                    balance: Math.max(0, balance)
                });
                
                // Without prepayment calculation
                const interestNoPrepay = balanceNoPrepay * monthlyRate;
                let principalNoPrepay = emi - interestNoPrepay;
                if (principalNoPrepay > balanceNoPrepay) {
                    principalNoPrepay = balanceNoPrepay;
                }
                balanceNoPrepay -= principalNoPrepay;
                totalInterestNoPrepay += interestNoPrepay;
                
                scheduleNoPrepay.push({
                    month,
                    emi: emi,
                    principal: principalNoPrepay,
                    interest: interestNoPrepay,
                    balance: Math.max(0, balanceNoPrepay)
                });
                
                if (balanceNoPrepay <= 1) break;
            }
            
            this.amortizationSchedule = schedule;
            this.amortizationWithoutPrepayment = scheduleNoPrepay;
            this.totalInterest = totalInterestPaid;
            this.totalAmount = principal + totalInterestPaid;
            this.interestAmount = totalInterestPaid;
            this.actualTenure = month;
            
            // Calculate savings from prepayment
            const tenureWithoutPrepay = scheduleNoPrepay.length;
            const interestWithoutPrepay = totalInterestNoPrepay;
            this.savingsFromPrepayment = interestWithoutPrepay - totalInterestPaid;
        },

        getMonthlyBreakdown() {
            // Return monthly data directly from amortizationSchedule
            return this.amortizationSchedule.map((month, index) => ({
                period: index + 1,
                label: `Month ${index + 1}`,
                openingBalance: index === 0 ? this.principalAmount : this.amortizationSchedule[index - 1].balance,
                emiPaid: month.emi,
                principal: month.principal,
                interest: month.interest,
                prepayment: month.prepayment || 0,
                closingBalance: month.balance
            }));
        },

        getQuarterlyBreakdown() {
            const quarterly = [];
            let currentQuarter = { quarter: 1, label: 'Q1', emiPaid: 0, principal: 0, interest: 0, prepayment: 0, openingBalance: this.principalAmount, closingBalance: 0 };
            
            this.amortizationSchedule.forEach((month, index) => {
                currentQuarter.emiPaid += month.emi;
                currentQuarter.principal += month.principal;
                currentQuarter.interest += month.interest;
                currentQuarter.prepayment += month.prepayment;
                currentQuarter.closingBalance = month.balance;
                
                if ((index + 1) % 3 === 0 || index === this.amortizationSchedule.length - 1) {
                    quarterly.push({ ...currentQuarter });
                    const nextQuarter = currentQuarter.quarter + 1;
                    currentQuarter = {
                        quarter: nextQuarter,
                        label: 'Q' + nextQuarter,
                        emiPaid: 0,
                        principal: 0,
                        interest: 0,
                        prepayment: 0,
                        openingBalance: month.balance,
                        closingBalance: 0
                    };
                }
            });
            
            return quarterly;
        },

        getYearlyBreakdown() {
            const yearly = [];
            let currentYear = { year: 1, label: 'Year 1', emiPaid: 0, principal: 0, interest: 0, prepayment: 0, openingBalance: this.principalAmount, closingBalance: 0 };
            
            this.amortizationSchedule.forEach((month, index) => {
                currentYear.emiPaid += month.emi;
                currentYear.principal += month.principal;
                currentYear.interest += month.interest;
                currentYear.prepayment += month.prepayment;
                currentYear.closingBalance = month.balance;
                
                if ((index + 1) % 12 === 0 || index === this.amortizationSchedule.length - 1) {
                    yearly.push({ ...currentYear });
                    const nextYear = currentYear.year + 1;
                    currentYear = {
                        year: nextYear,
                        label: 'Year ' + nextYear,
                        emiPaid: 0,
                        principal: 0,
                        interest: 0,
                        prepayment: 0,
                        openingBalance: month.balance,
                        closingBalance: 0
                    };
                }
            });
            
            return yearly;
        },

        getAmortizationBreakdown() {
            // Return the appropriate breakdown based on emiType
            if (this.emiType === 'monthly') {
                return this.getMonthlyBreakdown();
            } else if (this.emiType === 'quarterly') {
                return this.getQuarterlyBreakdown();
            } else {
                return this.getYearlyBreakdown();
            }
        },

        formatCurrency(amount) {
            const curr = this.currencies[this.currency];
            
            // Cache the formatter instance for better performance
            const cacheKey = `${this.currency}_${curr.locale}`;
            if (!this._formatters) this._formatters = {};
            if (!this._formatters[cacheKey]) {
                this._formatters[cacheKey] = new Intl.NumberFormat(curr.locale, {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0
                });
            }
            
            const formatted = this._formatters[cacheKey].format(Math.round(amount));
            
            if (!curr.symbol) return formatted;
            
            // Respect currency position (prefix or postfix)
            if (curr.position === 'postfix') {
                return formatted + ' ' + curr.symbol;
            } else {
                return curr.symbol + ' ' + formatted;
            }
        },

        formatCurrencyForPDF(amount) {
            const curr = this.currencies[this.currency];
            
            // Cache the PDF formatter instance
            const cacheKey = 'pdf_en-US';
            if (!this._formatters) this._formatters = {};
            if (!this._formatters[cacheKey]) {
                this._formatters[cacheKey] = new Intl.NumberFormat('en-US', {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0
                });
            }
            
            const formatted = this._formatters[cacheKey].format(Math.round(amount));
            
            if (!curr.pdfSymbol) return formatted;
            
            // Respect currency position (prefix or postfix)
            if (curr.position === 'postfix') {
                return formatted + ' ' + curr.pdfSymbol;
            } else {
                return curr.pdfSymbol + ' ' + formatted;
            }
        },

        formatNumber(num) {
            return Math.round(num).toLocaleString('en-IN');
        },

        renderChart() {
            const canvas = document.getElementById('emiChart');
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            
            // Destroy existing chart
            if (this.chart) {
                this.chart.destroy();
            }
            
            this.chart = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Principal Amount', 'Total Interest'],
                    datasets: [{
                        data: [this.principalAmount, this.interestAmount],
                        backgroundColor: ['#10b981', '#ef4444'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                padding: 20,
                                font: { size: 14, weight: '600' },
                                color: '#374151'
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const label = context.label || '';
                                    const value = this.formatCurrency(context.parsed);
                                    const percentage = ((context.parsed / this.totalAmount) * 100).toFixed(1);
                                    return `${label}: ${value} (${percentage}%)`;
                                }
                            }
                        }
                    }
                }
            });
        },

        async switchTab(tab) {
            this.activeTab = tab;
            if (tab === 'chart') {
                // Load Chart.js library on-demand when chart tab is activated
                this.chartLoading = true;
                try {
                    await loadChartLibrary();
                    this.$nextTick(() => this.renderChart());
                } catch (error) {
                    console.error('Failed to load Chart.js:', error);
                    alert('Failed to load chart library. Please try again.');
                } finally {
                    this.chartLoading = false;
                }
            }
        },

        async downloadPDF() {
            // Load jsPDF library on-demand
            this.pdfLoading = true;
            try {
                await loadPDFLibrary();
            } catch (error) {
                console.error('Failed to load jsPDF:', error);
                alert('Failed to load PDF library. Please try again.');
                this.pdfLoading = false;
                return;
            }
            
            const doc = new jsPDF();
            
            // Set default font to helvetica for better spacing
            doc.setFont('helvetica');
            
            // Title
            doc.setFontSize(20);
            doc.setFont(undefined, 'bold');
            doc.text('Loan EMI Calculation Report', 105, 20, { align: 'center' });
            
            // Date
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, 28, { align: 'center' });
            
            // Input Details
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('Loan Details', 20, 40);
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            let y = 48;
            doc.text('Loan Amount: ' + this.formatCurrencyForPDF(this.loanAmount), 20, y);
            y += 6;
            doc.text(`Interest Rate: ${this.interestRate}% per annum`, 20, y);
            y += 6;
            doc.text(`Tenure: ${this.tenureYears} years`, 20, y);
            y += 6;
            if (this.downPayment > 0) {
                doc.text('Down Payment: ' + this.formatCurrencyForPDF(this.downPayment), 20, y);
                y += 6;
            }
            if (this.processingFee > 0) {
                doc.text('Processing Fee (incl. GST): ' + this.formatCurrencyForPDF(this.processingFeeWithGst), 20, y);
                y += 6;
            }
            
            // Summary
            y += 5;
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('Summary', 20, y);
            y += 8;
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('Monthly EMI: ' + this.formatCurrencyForPDF(this.monthlyEmi), 20, y);
            y += 6;
            doc.text('Total Interest: ' + this.formatCurrencyForPDF(this.interestAmount), 20, y);
            y += 6;
            doc.text('Total Amount Payable: ' + this.formatCurrencyForPDF(this.totalAmount), 20, y);
            y += 6;
            doc.text('Effective Interest Rate: ' + this.effectiveRate + '%', 20, y);
            
            if (this.savingsFromPrepayment > 0) {
                y += 6;
                doc.setTextColor(16, 185, 129);
                doc.text('Savings from Prepayment: ' + this.formatCurrencyForPDF(this.savingsFromPrepayment), 20, y);
                doc.setTextColor(0, 0, 0);
            }
            
            // Breakdown Table
            y += 10;
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            const breakdownTitle = this.emiType === 'monthly' ? 'Monthly Breakdown' : 
                                   (this.emiType === 'quarterly' ? 'Quarterly Breakdown' : 'Yearly Breakdown');
            doc.text(breakdownTitle, 20, y);
            y += 8;
            
            const breakdownData = this.getAmortizationBreakdown();
            const periodLabel = this.emiType === 'monthly' ? 'Month' : 
                               (this.emiType === 'quarterly' ? 'Quarter' : 'Year');
            const tableData = breakdownData.map(period => [
                period.label,
                this.formatCurrencyForPDF(period.openingBalance),
                this.formatCurrencyForPDF(period.emiPaid),
                this.formatCurrencyForPDF(period.principal),
                this.formatCurrencyForPDF(period.interest),
                this.formatCurrencyForPDF(period.closingBalance)
            ]);
            
            doc.autoTable({
                startY: y,
                head: [[periodLabel, 'Opening', 'EMI Paid', 'Principal', 'Interest', 'Closing']],
                body: tableData,
                theme: 'grid',
                headStyles: { fillColor: [37, 99, 235], fontSize: 9 },
                bodyStyles: { fontSize: 8 },
                margin: { bottom: 25 },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 30 },
                    2: { cellWidth: 30 },
                    3: { cellWidth: 30 },
                    4: { cellWidth: 30 },
                    5: { cellWidth: 30 }
                }
            });
            
            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.setTextColor(128);
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
                doc.text('Generated by ToolZen.net', 105, 290, { align: 'center' });
            }
            
            doc.save('loan-emi-calculation.pdf');
            this.pdfLoading = false;
        },

        async downloadExcel() {
            // Load XLSX library on-demand
            this.excelLoading = true;
            try {
                await loadXLSXLibrary();
            } catch (error) {
                console.error('Failed to load XLSX:', error);
                alert('Failed to load Excel library. Please try again.');
                this.excelLoading = false;
                return;
            }
            
            const breakdownData = this.getAmortizationBreakdown();
            const periodLabel = this.emiType === 'monthly' ? 'Month' : 
                               (this.emiType === 'quarterly' ? 'Quarter' : 'Year');
            const breakdownTitle = this.emiType === 'monthly' ? 'Monthly Breakdown' : 
                                   (this.emiType === 'quarterly' ? 'Quarterly Breakdown' : 'Yearly Breakdown');
            
            // Prepare data for Excel
            const data = [
                ['Loan EMI Calculation Report'],
                [''],
                ['Loan Details'],
                ['Loan Amount', this.loanAmount],
                ['Interest Rate', this.interestRate + '%'],
                ['Tenure', this.tenureYears + ' years'],
                ['EMI Type', this.emiType.charAt(0).toUpperCase() + this.emiType.slice(1)],
                ['Down Payment', this.downPayment],
                ['Processing Fee (incl. GST)', this.processingFeeWithGst],
                [''],
                ['Summary'],
                ['Monthly EMI', this.monthlyEmi],
                ['Total Interest', this.interestAmount],
                ['Total Amount Payable', this.totalAmount],
                ['Effective Interest Rate', this.effectiveRate + '%'],
                ['Savings from Prepayment', this.savingsFromPrepayment],
                [''],
                [breakdownTitle],
                [periodLabel, 'Opening Balance', 'EMI Paid', 'Principal', 'Interest', 'Prepayment', 'Closing Balance']
            ];
            
            breakdownData.forEach(period => {
                data.push([
                    period.label,
                    period.openingBalance,
                    period.emiPaid,
                    period.principal,
                    period.interest,
                    period.prepayment || 0,
                    period.closingBalance
                ]);
            });
            
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'EMI Calculation');
            XLSX.writeFile(wb, 'loan-emi-calculation.xlsx');
            this.excelLoading = false;
        },

        saveToHistory() {
            if (this.monthlyEmi === 0) return;
            
            const record = {
                loanAmount: this.loanAmount,
                interestRate: this.interestRate,
                tenure: this.tenureYears,
                monthlyEmi: this.monthlyEmi,
                totalInterest: this.interestAmount,
                totalAmount: this.totalAmount,
                date: new Date().toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                })
            };
            
            this.history.unshift(record);
            if (this.history.length > 5) this.history.pop();
            localStorage.setItem('emi_history', JSON.stringify(this.history));
        },

        clearHistory() {
            this.history = [];
            localStorage.removeItem('emi_history');
        },

        reset() {
            this.loanAmount = 1000000;
            this.interestRate = 10;
            this.tenureYears = 10;
            this.downPayment = 0;
            this.processingFee = 0;
            this.prepaymentAmount = 0;
            this.extraEmi = 0;
            this.showAdvanced = false;
            this.calculate();
        }
    };
}

if (typeof window.Alpine !== 'undefined') {
    Alpine.data('loanEmiCalculator', loanEmiCalculator);
} else {
    document.addEventListener('alpine:init', () => {
        Alpine.data('loanEmiCalculator', loanEmiCalculator);
    });
}
